import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Info } from "lucide-react";
import { createUserAction } from "../../slices/usersSlice";
import MainPageWrapper from "../../../../../../components/MainPageWrapper/MainPageWrapper";
import { RootState } from "../../../../../../app/rootReducer";
import { useNotification } from "../../../../../../context/NotificationContext";
import { useNavigate } from "react-router-dom";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  createUserSchema,
  type CreateUserFormData,
} from "../../schemas/userSchemas";
import { getUserFormDetails } from "../../api/getUserFormDetailsApi";
import { Role } from "../../api/types";

const CreateUser: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state: RootState) => state.users);
  const { showNotification } = useNotification();
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [loadingFormDetails, setLoadingFormDetails] = useState(true);
  const [formDetailsError, setFormDetailsError] = useState<string | null>(null);

  const form = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      businessRoleId: undefined,
    },
    mode: "onChange",
  });

  const { control, handleSubmit, formState, setValue, watch } = form;
  const { isSubmitting, isValid } = formState;
  const selectedRoleId = watch("businessRoleId");

  useEffect(() => {
    const fetchFormDetails = async () => {
      try {
        setLoadingFormDetails(true);
        const response = await getUserFormDetails();
        setAvailableRoles(response.data.availableRoles);
      } catch (err) {
        setFormDetailsError(
          err instanceof Error ? err.message : "Failed to load form details",
        );
      } finally {
        setLoadingFormDetails(false);
      }
    };
    fetchFormDetails();
  }, []);

  const onSubmit = async (values: CreateUserFormData) => {
    dispatch(
      createUserAction(
        {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          businessRoleId: values.businessRoleId,
        },
        (newUser) => {
          showNotification(
            "Successfully created user!",
            "The user information has been created.",
            "success",
          );
          navigate(`/hub/users`);
        },
      ),
    );
  };

  // Filter out roles that require CertainPath assigner (e.g., Coach) - non-CP companies can't assign these
  const selectableRoles = availableRoles.filter((role) => !role.requiresCertainPathAssigner);
  const selectedRole = selectableRoles.find((r) => r.id === selectedRoleId) || null;

  return (
    <MainPageWrapper
      error={error || formDetailsError}
      loading={loading || isSubmitting || loadingFormDetails}
      title="Create User"
    >
      <Form {...form}>
        <form className="space-y-6 bg-white" onSubmit={handleSubmit(onSubmit)}>
          <div className="text-sm text-muted-foreground mb-2">
            Fields marked with an asterisk (*) are required.
          </div>
          <FormField
            control={control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Enter first name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Enter last name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email *</FormLabel>
                <FormControl>
                  <Input placeholder="Enter email address" type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="businessRoleId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Business Role *</FormLabel>
                <Select
                  onValueChange={(value) => {
                    const parsed = parseInt(value, 10);
                    if (!isNaN(parsed)) {
                      field.onChange(parsed);
                    }
                  }}
                  value={field.value ? field.value.toString() : undefined}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a role..." />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {selectableRoles.map((role) => (
                      <SelectItem key={role.id} value={role.id.toString()}>
                        {role.label}
                        {role.isCertainPathOnly && (
                          <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                            CP
                          </span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedRole?.description && (
                  <div className="flex items-start gap-2 mt-2 p-3 bg-slate-50 rounded-md border border-slate-200">
                    <Info className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
                    <p className="text-sm text-slate-600">{selectedRole.description}</p>
                  </div>
                )}
                {selectedRole?.permissions && selectedRole.permissions.length > 0 && (
                  <div className="mt-3 p-3 bg-slate-50 rounded-md border border-slate-200">
                    <p className="text-sm font-medium text-slate-700 mb-2">This role includes:</p>
                    <ul className="text-sm text-slate-600 space-y-1">
                      {selectedRole.permissions.map((permission) => (
                        <li key={permission.id} className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                          {permission.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex justify-end gap-4 pt-4">
            <Button onClick={() => navigate("/hub/users")} type="button" variant="outline">
              Cancel
            </Button>
            <Button disabled={!isValid || isSubmitting} type="submit">
              {isSubmitting || loading ? "Creating..." : "Create User"}
            </Button>
          </div>
        </form>
      </Form>
    </MainPageWrapper>
  );
};

export default CreateUser;
